/** Alle Aufträge — server-filtered list s URL state filtry.
 *  V0.1 jednoduché filtry (status multi, search). TanStack Table přijde v0.2. */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { StatusBadge } from "@/components/ui/badge";
import { Empty } from "@/components/ui/empty";
import { enrichOrdersWithEndClient, fetchOrders } from "@/lib/fetchers";
import { fmtEur } from "@/lib/format";
import { orderStatusMeta } from "@/lib/status";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import type { PartnerOrder } from "@/lib/types";

const STATUS_OPTIONS: PartnerOrder["status_label"][] = [
  "geplant",
  "in_arbeit",
  "abgeschlossen",
  "reklamiert",
  "storniert",
];

export function OrdersRoute() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  const statuses = useMemo(() => {
    const raw = params.get("status");
    if (!raw) return [] as PartnerOrder["status_label"][];
    return raw
      .split(",")
      .filter((s): s is PartnerOrder["status_label"] =>
        STATUS_OPTIONS.includes(s as PartnerOrder["status_label"]),
      );
  }, [params]);

  const search = params.get("q") || "";

  const ordersQ = useQuery({
    queryKey: queryKeys.ordersAll({ statuses, search }),
    queryFn: async () => {
      const list = await fetchOrders({
        statuses: statuses.length ? statuses : undefined,
        search: search || undefined,
        orderBy: { column: "scheduled_date", ascending: false },
      });
      return enrichOrdersWithEndClient(list);
    },
  });

  const toggleStatus = (s: PartnerOrder["status_label"]) => {
    const next = new Set(statuses);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    const arr = Array.from(next);
    if (arr.length === 0) params.delete("status");
    else params.set("status", arr.join(","));
    setParams(params, { replace: true });
  };

  const submitSearch = () => {
    if (searchInput) params.set("q", searchInput);
    else params.delete("q");
    setParams(params, { replace: true });
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput("");
  };

  const orders = ordersQ.data || [];

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 className="display" style={{ margin: 0, fontSize: 28 }}>
            {t.orderList.title}
          </h1>
          <p style={{ marginTop: 6, fontSize: 13, color: "var(--text-3)" }}>
            {orders.length} Aufträge {statuses.length || search ? "(gefiltert)" : ""}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ position: "relative", width: 280 }}>
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-3)",
              }}
            >
              <Ic.Search size={14} />
            </span>
            <input
              className="input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              onBlur={submitSearch}
              placeholder="Auftrag, Kunde, Stadt…"
              style={{ height: 34, paddingLeft: 36, fontSize: 13 }}
            />
          </div>
          {(statuses.length > 0 || search) && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              <Ic.Close size={12} /> Zurücksetzen
            </Button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {STATUS_OPTIONS.map((s) => {
          const meta = orderStatusMeta(s);
          const active = statuses.includes(s);
          if (!meta) return null;
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className={`badge ${active ? meta.badgeClass : ""}`}
              style={{
                cursor: "pointer",
                opacity: active ? 1 : 0.7,
                background: active ? undefined : "var(--bg-card-2)",
                border: active ? undefined : "1px solid var(--border)",
                color: active ? undefined : "var(--text-2)",
                padding: "5px 10px",
              }}
            >
              {meta.label}
            </button>
          );
        })}
      </div>

      {orders.length === 0 ? (
        <Empty title={t.orderList.empty} />
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Auftrag</th>
                <th>Datum</th>
                <th>Klient</th>
                <th>Stadt</th>
                <th>System</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Wert</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className="row-clickable"
                  onClick={() => navigate(`/orders/${o.id}`)}
                >
                  <td className="num" style={{ fontSize: 12, color: "var(--text-2)" }}>
                    {o.deal_number || o.title || o.id.slice(0, 8)}
                  </td>
                  <td className="num" style={{ fontSize: 12.5, color: "var(--text-2)" }}>
                    {o.scheduled_date ? formatShort(o.scheduled_date) : "—"}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {o.installation_contact_name || o.end_client_label || o.title || "—"}
                    </div>
                    {o.end_client_label && o.installation_contact_name && (
                      <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                        {o.end_client_label}
                      </div>
                    )}
                  </td>
                  <td style={{ color: "var(--text-2)" }}>{o.installation_city || "—"}</td>
                  <td style={{ color: "var(--text-2)" }}>{o.system_type || "—"}</td>
                  <td>
                    <StatusBadge meta={orderStatusMeta(o.status_label)} />
                  </td>
                  <td className="right num" style={{ fontWeight: 500 }}>
                    {fmtEur(o.value_eur)}
                  </td>
                  <td style={{ width: 28, color: "var(--text-3)" }}>
                    <Ic.Chevron size={14} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatShort(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear() % 100}`;
}
